/* Copyright (c) 2026 Health Endeavors, LLC. All Rights Reserved */

import { DocumentVersionType } from './algorithm/types';
import styles from './MetaInfo.module.scss';
import { documentVersionStateToShortStrMap, formatDate, formatDistanceDay } from './utils';

export const complexity = [
  {
    id: '1',
    title: 'Simple',
  },
  {
    id: '4',
    title: 'Standard',
  },
  {
    id: '9',
    title: 'Complex',
  },
];

export function ContributorTr({
  members,
  needGrab,
}: {
  members: { role: string; name?: string }[];
  needGrab?: boolean;
}) {
  const contributors = members
    ?.filter((member) => member.role === 'CONTRIBUTOR' && member.name)
    .map((member) => member.name);
  if (!contributors || contributors.length === 0) return <></>;
  return (
    <tr>
      {needGrab ? <td style={{ minWidth: '26px' }} /> : undefined}
      {needGrab ? <td style={{ minWidth: '50px' }} /> : undefined}
      <th style={{ paddingLeft: needGrab ? '14px' : '0' }}>Contributor{contributors.length > 1 && 's'}:</th>
      <td>{contributors.join(', ')}</td>
    </tr>
  );
}

function getValue(title: string, value: string) {
  switch (title?.toLowerCase()) {
    case 'next maintenance':
    case 'target date':
    case 'contextualized on':
    case 'published':
    case 'revised':
      return (
        <>
          <span className={styles.date}>{formatDate(value)}</span>
          <span className={styles.relativeDate}>{formatDistanceDay(new Date(value))}</span>
        </>
      );
    case 'complexity':
      return complexity.find(({ id }) => id === value)?.title;

    default:
      return value;
  }
}
interface MetaInfoProps {
  children?: React.ReactNode;
  documentVersion: DocumentVersionType;
  filter?: string[];
}

export function MetaInfo({ children, documentVersion, filter }: MetaInfoProps) {
  if (!documentVersion) return <></>;
  const status = documentVersion.state;

  return (
    <table className={styles.table}>
      <tbody>
        {(!filter || filter.includes('Owner')) && (
          <tr>
            <th>Owner:</th>
            <td>{documentVersion.document.owner.name}</td>
          </tr>
        )}
        {(!filter || filter.includes('status')) && status && (
          <tr>
            <th>Status:</th>
            <td>{documentVersionStateToShortStrMap[status]}</td>
          </tr>
        )}
        {(!filter || filter.includes('Contributor')) && <ContributorTr members={documentVersion.document.members} />}
        {documentVersion.docCredits
          ?.filter((credit) => !filter || filter.includes(credit.title))
          .map((credit, index) => (
            <tr key={index}>
              <th>{credit.title}:</th>
              <td>{getValue(credit.title, credit.name)}</td>
            </tr>
          ))}
        {children}
      </tbody>
    </table>
  );
}
